import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { INSTALLATION_STATUS_LABELS, LoadStatus, InstallationStatus } from "@/config/constants";
import { toast } from "sonner";
import { queueAction } from "@/lib/offline/action-queue";

export function useMyLoadsMutations() {
  const queryClient = useQueryClient();
  
  // SMS notification state
  const [notifiedInstallations, setNotifiedInstallations] = useState<Set<string>>(new Set());
  const [sendingSmsFor, setSendingSmsFor] = useState<string | null>(null);

  // Update load status (with offline support)
  const updateLoadStatusMutation = useMutation({
    mutationFn: async ({ loadId, status, updatedAt }: { 
      loadId: string; 
      status: LoadStatus;
      updatedAt?: string;
    }) => {
      // If offline, queue the action
      if (!navigator.onLine) {
        await queueAction({
          type: 'UPDATE_LOAD_STATUS',
          payload: { 
            loadId, 
            status, 
            updatedAt: updatedAt || new Date().toISOString() 
          },
        });
        return; // Don't throw - action is queued
      }
      
      const { error } = await supabase
        .from("installation_loads")
        .update({ status })
        .eq("id", loadId);
      if (error) throw error;
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["my-loads"] });
      
      // Snapshot previous value
      const previousData = queryClient.getQueryData(["my-loads"]);
      
      // Optimistically update
      queryClient.setQueryData(["my-loads"], (old: unknown) => {
        if (!Array.isArray(old)) return old;
        return old.map((load: { id: string; status: string }) => 
          load.id === variables.loadId 
            ? { ...load, status: variables.status }
            : load
        );
      });
      
      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(["my-loads"], context.previousData);
      }
      toast.error("Kuorman tilan päivitys epäonnistui");
    },
    onSuccess: () => {
      if (navigator.onLine) {
        queryClient.invalidateQueries({ queryKey: ["my-loads"] });
      }
      toast.success("Kuorman tila päivitetty");
    },
  });

  // Update installation status (with offline support)
  const updateInstallationMutation = useMutation({
    mutationFn: async ({ installationId, status, notes, updatedAt }: { 
      installationId: string; 
      status: string; 
      notes?: string;
      updatedAt?: string;
    }) => {
      // If offline, queue the action
      if (!navigator.onLine) {
        await queueAction({
          type: 'UPDATE_INSTALLATION_STATUS',
          payload: { 
            installationId, 
            status, 
            notes,
            updatedAt: updatedAt || new Date().toISOString() 
          },
        });
        toast.info("Toiminto tallennettu - synkronoidaan kun verkko palaa");
        return; // Don't throw - action is queued
      }
      
      const updates: Record<string, unknown> = { status };
      if (status === "completed") {
        updates.completed_date = new Date().toISOString().split("T")[0];
      }
      if (notes) {
        updates.notes = notes;
      }
      
      const { error } = await supabase
        .from("installations")
        .update(updates)
        .eq("id", installationId);
      if (error) throw error;
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["my-load-installations"] });
      
      // Snapshot previous value
      const previousData = queryClient.getQueryData(["my-load-installations"]);
      
      // Optimistically update the installations map
      queryClient.setQueryData(["my-load-installations"], (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const map = old as Record<string, Array<{ id: string; status: string; notes?: string }>>;
        const newMap: Record<string, Array<{ id: string; status: string; notes?: string }>> = {};
        
        for (const [loadId, installations] of Object.entries(map)) {
          newMap[loadId] = installations.map(inst => 
            inst.id === variables.installationId 
              ? { ...inst, status: variables.status, notes: variables.notes || inst.notes }
              : inst
          );
        }
        
        return newMap;
      });
      
      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error (but not for offline queue errors)
      if (context?.previousData && navigator.onLine) {
        queryClient.setQueryData(["my-load-installations"], context.previousData);
        toast.error("Tehtävän tilan päivitys epäonnistui");
      }
    },
    onSuccess: (_, variables) => {
      if (navigator.onLine) {
        queryClient.invalidateQueries({ queryKey: ["my-load-installations"] });
        queryClient.invalidateQueries({ queryKey: ["my-loads"] });
        queryClient.invalidateQueries({ queryKey: ["claimed-pickups"] });
      }
      const statusText = INSTALLATION_STATUS_LABELS[variables.status as InstallationStatus] || variables.status;
      toast.success(`Tehtävä merkitty: ${statusText}`);
    },
  });

  // Send SMS notification to customer
  const handleSendSms = async (installationId: string, orderId: string) => {
    setSendingSmsFor(installationId);
    try {
      const response = await supabase.functions.invoke('send-sms-notification', {
        body: { orderId, notificationType: 'installer_departed' },
      });
      
      if (response.error) throw response.error;
      
      setNotifiedInstallations(prev => new Set(prev).add(installationId));
      toast.success("Viesti lähetetty asiakkaalle!");
    } catch (error) {
      console.error("SMS sending failed:", error);
      toast.error("Viestin lähetys epäonnistui");
    } finally {
      setSendingSmsFor(null);
    }
  };

  // Mark pickup as completed and send SMS notification
  const handlePickupCompleted = async (installationId: string, orderId: string) => {
    try {
      await updateInstallationMutation.mutateAsync({ installationId, status: "completed" });
      
      // Send SMS notification (non-blocking)
      supabase.functions.invoke('send-sms-notification', {
        body: { orderId, notificationType: 'pickup_completed' },
      }).catch(err => console.error("Pickup SMS error:", err));
      
      toast.success("Nouto merkitty valmiiksi ja viesti lähetetty asiakkaalle");
    } catch (error) {
      console.error("Failed to complete pickup:", error);
      toast.error("Virhe noudon kuittauksessa");
    }
  };

  // Helper functions for components
  const updateLoadStatus = (loadId: string, status: LoadStatus, updatedAt?: string) => {
    updateLoadStatusMutation.mutate({ loadId, status, updatedAt });
  };

  const updateInstallationStatus = (
    installationId: string, 
    status: string, 
    notes?: string,
    updatedAt?: string
  ) => {
    // Always pass updatedAt for conflict detection - critical for offline sync
    updateInstallationMutation.mutate({ 
      installationId, 
      status, 
      notes, 
      updatedAt: updatedAt || new Date().toISOString()
    });
  };

  return {
    // Mutations
    updateLoadStatus,
    updateInstallationStatus,
    handleSendSms,
    handlePickupCompleted,
    
    // State
    sendingSmsFor,
    notifiedInstallations,
    
    // Loading states
    isUpdatePending: updateInstallationMutation.isPending,
    isLoadStatusPending: updateLoadStatusMutation.isPending,
  };
}
